
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import NoteModal from '@/components/modals/NoteModal.vue'
import Toaster from '@meforma/vue-toaster'
import NoteTile from '@/components/elements/NoteTile.vue'
import { CustomerService } from '@/services/customer.service'
import { NoteService } from '@/services/note.service'
import { CustomerDetails } from '@/@types/responses/customer/CustomerDetails'
import { Note } from '@/@types/responses/note/Note'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import PhoneNumberInput from '@/components/shared/PhoneNumberInput.vue'
import { CustomerMessage } from '@/@types/messages/CustomerMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import Dropdown from '@/components/shared/Dropdown.vue'
import { StateService } from '@/services/states.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'Customer',
  components: {
    AuditInformation,
    Dropdown,
    LoadingPulse,
    LockingButton,
    NoteModal,
    NoteTile,
    PhoneNumberInput,
    TextInput
  },
  beforeRouteLeave() {
    if (this.showNoteModal) {
      this.showNoteModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const customerService = inject(PROVIDER_CONSTANTS.customerService) as CustomerService
    const noteService = inject(PROVIDER_CONSTANTS.noteService) as NoteService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      customerService,
      noteService,
      stateService,
      toast
    }
  },
  data() {
    let noteToEdit: Note|undefined
    let customer: CustomerDetails|undefined
    let unchangedCustomer: CustomerDetails|undefined

    return {
      loading: true,
      customer,
      unchangedCustomer,
      saving: false,
      showNoteModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      noteToEdit
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.customer
      const b = this.unchangedCustomer

      return (a?.firstName !== b?.firstName ||
              a?.lastName !== b?.lastName ||
              a?.email !== b?.email ||
              a?.phoneNumber !== b?.phoneNumber ||
              a?.addressLine1 !== b?.addressLine1 ||
              a?.addressLine2 !== b?.addressLine2 ||
              a?.city !== b?.city ||
              a?.stateId !== b?.stateId ||
              a?.postalCode !== b?.postalCode) && 
              a?.firstName.trim() !== ''
    },
    titleText(): string {
      return !this.customer || this.customer.id === 0 ? 'Create Customer' : 'Edit Customer'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    this.getStates().then(() => {
      if (this.id > 0) {
        this.getCustomer()
      } else {
        this.customer = new CustomerDetails(0)
        this.unchangedCustomer = new CustomerDetails(0)
        this.loading = false
      }
    })
  },
  methods: {
    getCustomer(): Promise<void> {
      return this.customerService.getCustomer(this.customer ? this.customer.id : this.id).then(customer => {
        this.customer = customer
        this.unchangedCustomer = JSON.parse(JSON.stringify(customer))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the customer. Try reloading the page.')
        }
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onCreateNoteClicked(): void {
      this.noteToEdit = new Note(0)
      this.showNoteModal = true
    },
    onDeleteNote(note: Note): void {
      this.noteService.deleteNote(note.id).then(() => {
        this.getCustomer()
        this.toast.success('The note was deleted successfully.')
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the note. Please try again.')
        }
      })
    },
    onEditNote(note: Note): void {
      this.noteToEdit = note
      this.showNoteModal = true
    },
    onSaveCustomerClicked(): void {
      if (!this.customer) return

      this.saving = true

      const customerMessage = new CustomerMessage(this.customer.firstName, this.customer.lastName, this.customer.phoneNumber, this.customer.email, this.customer.addressLine1, this.customer.addressLine2, this.customer.city, this.customer.stateId, this.customer.postalCode)
      if (this.customer.id === 0)
        this.customerService.createCustomer(customerMessage).then(objectCreatedResponse => {
          if (!this.customer) return

          this.customer.id = objectCreatedResponse.id
          this.getCustomer().then(() => {
            this.$router.replace(`/customer/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The customer was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the customer. Please try again.')
          }
        })
      else
        this.customerService.updateCustomer(this.id, customerMessage).then(() => {
          this.saving = false
          this.toast.success('The customer was updated successfully.')
          this.$router.push('/customers')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the customer. Please try again.')
          }
        })
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.customer)
        document.title = `${this.customer.firstName} ${this.customer.lastName} - Short Action Customs, LLC`
    }
  }
})
